import { memo } from 'react'

import { Box, Button, Menu, MenuItem, Tooltip } from '@mui/material'
import { IconChevronDown, IconMail } from '@tabler/icons-react'
import type { Draft } from 'types/graphql'

import SlackLogo from 'src/components/Slack/SlackLogo/SlackLogo'
import TemplateIcon from 'src/components/Templates/TemplateIcon/TemplateIcon'
import { TemplateTypeMetadata } from 'src/lib/objects'

import ComposeEmailDraftHeader from '../Email/ComposeEmailDraftHeader/ComposeEmailDraftHeader'
import ComposeSlackDraftHeader from '../Slack/ComposeSlackDraftHeader/ComposeSlackDraftHeader'

const channelMenuItemSx = {
  height: 40,
  justifyContent: 'flex-start',
}

const ComposeDraftHeaderComponent = ({
  draft,
  handleUpdateEmailFrom,
  handleUpdateEmailSubject,
  handleAddEmailRecipient,
  handleRemoveEmailRecipient,
  errorSaving,
}: {
  draft: Draft
  handleUpdateEmailFrom: (from: string) => void
  handleUpdateEmailSubject: (subject: string) => void
  handleAddEmailRecipient: (
    recipient: string,
    field: 'to' | 'cc' | 'bcc'
  ) => void
  handleRemoveEmailRecipient: (
    recipient: string,
    field: 'to' | 'cc' | 'bcc'
  ) => void
  errorSaving: boolean
}) => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)

  const showChannelPicker = false

  const inner = () => {
    switch (draft.channel) {
      case 'EMAIL':
        return (
          <ComposeEmailDraftHeader
            draft={draft}
            handleUpdateEmailFrom={handleUpdateEmailFrom}
            handleUpdateEmailSubject={handleUpdateEmailSubject}
            handleAddEmailRecipient={handleAddEmailRecipient}
            handleRemoveEmailRecipient={handleRemoveEmailRecipient}
            errorSaving={errorSaving}
          />
        )
      case 'SLACK':
        return (
          <ComposeSlackDraftHeader
            draft={draft}
            setDraft={() => {}}
          />
        )
    }
  }

  const handleChannelPickerClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleChannelPickerClose = () => {
    setAnchorEl(null)
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'flex-start',
        mx: 1,
        my: 1,
      }}
    >
      {showChannelPicker && (
        <Box sx={{ width: '200px' }}>
          <Button
            id="channel-picker"
            startIcon={<TemplateIcon templateType={draft.channel} />}
            endIcon={<IconChevronDown />}
            onClick={handleChannelPickerClick}
          >
            {TemplateTypeMetadata[draft.channel].label}
          </Button>
          <Menu
            id="channel-picker-menu"
            anchorEl={anchorEl}
            open={open}
            onClose={handleChannelPickerClose}
          >
            <MenuItem
              onClick={() => {
                setAnchorEl(null)
              }}
              sx={channelMenuItemSx}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <IconMail size={16} />
                Email
              </Box>
            </MenuItem>
            <Tooltip title="Coming soon">
              <Box>
                <MenuItem
                  sx={channelMenuItemSx}
                  onClick={() => {
                    setAnchorEl(null)
                  }}
                  disabled
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <SlackLogo size={16} />
                    Slack
                  </Box>
                </MenuItem>
              </Box>
            </Tooltip>
          </Menu>
        </Box>
      )}
      {/* Channel-specific header */}
      <Box sx={{ width: '100%', mr: 2 }}>{inner()}</Box>
    </Box>
  )
}

const ComposeDraftHeader = memo(ComposeDraftHeaderComponent)

export default ComposeDraftHeader
